import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";

interface Screenshot {
  id: string;
  image: string;
  caption?: string;
}

interface AppScreenshotsProps {
  items: Screenshot[];
}

/**
 * AppScreenshots
 *
 * Mobile app screenshot carousel with phone mockup frame.
 * Features autoplay, navigation arrows, and dot indicators.
 * Text from translations keyed by screenshot id.
 */
export function AppScreenshots({ items }: AppScreenshotsProps) {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const title = t("appScreenshots.title");
  const subtitle = t("appScreenshots.subtitle");

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  // Autoplay
  React.useEffect(() => {
    if (!emblaApi) return;

    const interval = setInterval(() => {
      emblaApi.scrollNext();
    }, 4000);

    return () => clearInterval(interval);
  }, [emblaApi]);

  // Track selected slide
  React.useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // Intersection observer for scroll animations
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollPrev = React.useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = React.useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const scrollTo = React.useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const getCaption = (item: Screenshot) =>
    t(`appScreenshots.${item.id}.caption`, { defaultValue: item.caption });

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "mx-auto mb-16 max-w-3xl text-center lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <SectionLabel className="mb-6">App Preview</SectionLabel>

          <h2 className="mb-6 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && (
            <p className="text-base leading-relaxed text-muted-foreground lg:text-lg">{subtitle}</p>
          )}
        </div>

        {/* Carousel */}
        <div
          className={cn(
            "relative mx-auto max-w-sm",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
          style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
        >
          {/* Phone mockup frame */}
          <div
            className={cn(
              "relative overflow-hidden rounded-[2.5rem]",
              "bg-muted",
              "border-[8px] border-border",
              "shadow-2xl shadow-black/10",
            )}
          >
            {/* Notch */}
            <div className="absolute top-2 left-1/2 z-10 h-6 w-24 -translate-x-1/2 rounded-full bg-black" />

            {/* Carousel container */}
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {items.map((screenshot) => (
                  <div key={screenshot.id} className="min-w-0 flex-[0_0_100%]">
                    <div className="flex aspect-[9/19] items-center justify-center bg-muted">
                      <img
                        src={screenshot.image}
                        alt={getCaption(screenshot)}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          // Fallback for missing images
                          e.currentTarget.style.display = "none";
                          e.currentTarget.parentElement!.innerHTML = `
                            <div class="flex items-center justify-center h-full text-muted-foreground text-sm p-4 text-center">
                              ${getCaption(screenshot)}
                            </div>
                          `;
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={scrollPrev}
            className={cn(
              "absolute top-1/2 left-0 -translate-x-12 -translate-y-1/2 lg:-translate-x-16",
              "flex h-10 w-10 items-center justify-center rounded-full",
              "border border-border bg-muted",
              "text-foreground transition-colors hover:bg-muted/80",
            )}
            aria-label="Previous screenshot"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            onClick={scrollNext}
            className={cn(
              "absolute top-1/2 right-0 translate-x-12 -translate-y-1/2 lg:translate-x-16",
              "flex h-10 w-10 items-center justify-center rounded-full",
              "border border-border bg-muted",
              "text-foreground transition-colors hover:bg-muted/80",
            )}
            aria-label="Next screenshot"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Caption */}
        <p
          className={cn(
            "mt-6 text-center text-sm text-muted-foreground lg:text-base",
            "transition-all duration-500",
            isVisible ? "opacity-100" : "opacity-0",
          )}
          style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}
        >
          {items[selectedIndex] && getCaption(items[selectedIndex])}
        </p>

        {/* Dot indicators */}
        <div
          className={cn(
            "mt-6 flex justify-center gap-2",
            "transition-all duration-500",
            isVisible ? "opacity-100" : "opacity-0",
          )}
          style={{ transitionDelay: isVisible ? "500ms" : "0ms" }}
        >
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={cn(
                "h-2 w-2 rounded-full transition-all duration-300",
                index === selectedIndex
                  ? "w-6 bg-primary"
                  : "bg-muted-foreground/20 hover:bg-muted-foreground/40",
              )}
              aria-label={`Go to screenshot ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
